import { useState, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { PlanDetailItem } from '@/types'

interface BulkActionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedItems: PlanDetailItem[]
  onConfirm: () => void
  isLoading: boolean
  title: string
  description: string
  confirmText: string
  loadingText: string
  confirmVariant?: 'default' | 'destructive'
}

const ITEMS_PER_PAGE = 5

export function BulkActionModal({
  open,
  onOpenChange,
  selectedItems,
  onConfirm,
  isLoading,
  title,
  description,
  confirmText,
  loadingText,
  confirmVariant = 'default',
}: BulkActionModalProps) {
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = Math.ceil(selectedItems.length / ITEMS_PER_PAGE)

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return selectedItems.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [selectedItems, currentPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  // Reset to page 1 when modal opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setCurrentPage(1)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden">
          <div
            className={cn(
              'border border-[#d6dae1] overflow-hidden',
              totalPages > 1 ? 'rounded-t-xl' : 'rounded-xl'
            )}
          >
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">
                    SKU #
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map(item => (
                  <tr key={item.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3 text-sm">
                      {item.skuNumber || '-'}
                    </td>
                    <td className="py-2 px-3 text-sm truncate max-w-[200px]">
                      {item.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-white border border-[#d6dae1] border-t-0 rounded-b-xl flex items-center justify-end p-2.5 gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={cn(
                    'w-6 h-6 text-xs rounded cursor-pointer transition-colors flex items-center justify-center',
                    page === currentPage
                      ? 'bg-white border border-[#d6dae1] text-[#1c1c1c]'
                      : 'text-[#556374] hover:bg-muted'
                  )}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-0.5 h-6 text-xs font-medium text-[#556374] hover:text-[#1c1c1c] cursor-pointer transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? loadingText : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
